/**
 * `/patients/workstation` → `/patients/register`, carrying the tab across.
 *
 * ── WHY ──────────────────────────────────────────────────────────────────
 *
 * Desk, 2026-09-07: "still showing old page…" — with the URL bar reading
 * `/patients/workstation?tab=register`.
 *
 * The sidebar row had already been moved to `/patients/register`, and the live
 * bundle was verified to carry it. But the OLD route was deliberately left
 * routed "for old bookmarks", and a bookmark, a browser history entry or an
 * autocompleted URL is exactly how somebody keeps arriving at it. Leaving a
 * superseded screen reachable is the same as not having replaced it: whoever
 * lands there sees the old page and reports it as unfixed, which is precisely
 * what happened.
 *
 * So the old address now FORWARDS instead of rendering. Nothing 404s, every
 * saved link keeps working, and there is one screen behind two addresses rather
 * than two screens.
 *
 * ── THE TAB IS TRANSLATED, NOT DROPPED ───────────────────────────────────
 *
 * The two pages name their tabs differently, and a redirect that ignored the
 * incoming `?tab=` would land everybody on the registration form — including
 * someone whose bookmark was specifically the patient list. Worse, the old
 * `register` id meant "Patient Search", while on the new page `register` means
 * the registration FORM: passing it through unchanged would send a saved link
 * for the patient list to the opposite screen, silently and plausibly.
 *
 *   old `registration`  the form            -> `register`
 *   old `register`      "Patient Search"    -> `list`      (the Patient Register)
 *   old `dashboard`     the removed panel   -> `dashboard`
 *   old `merge`         removed entirely    -> `register`
 *
 * `replace`, so Back returns to wherever the operator came from rather than to
 * this redirect, which would bounce them forward again.
 */
import type { JSX } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';

/** Old workstation tab id → the equivalent tab on `PatientRegistrationPage`. */
const TAB_MAP: Record<string, string> = {
  registration: 'register',
  // NOT a pass-through: `register` was the old shell's PATIENT SEARCH tab, and
  // it is the registration form on the new page. See the header.
  register: 'list',
  dashboard: 'dashboard',
  merge: 'register',
};

export function PatientWorkstationRedirect(): JSX.Element {
  const [params] = useSearchParams();
  const tab = TAB_MAP[params.get('tab') ?? ''] ?? 'register';
  return <Navigate to={`/patients/register?tab=${tab}`} replace />;
}

export default PatientWorkstationRedirect;
